// apps/web/src/components/chat/forms/CompanyImportForm.tsx
import { Button, Typography, Box, Alert, LinearProgress, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import { useState } from 'react';
import { getSupabaseClient } from '@lingaai/supabase-client';

interface CompanyImportFormProps {
  data: {
    fund_id: string;
  };
  tenantId: string;
  onSuccess: (result: { companies: number; contacts: number }) => void;
  onClose: () => void;
}

export function CompanyImportForm({ data, tenantId, onSuccess, onClose }: CompanyImportFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [createSubmissions, setCreateSubmissions] = useState<'none' | 'historical'>('none');
  const supabase = getSupabaseClient();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // 1. Upload file to storage
      setStatus('Uploading file...');
      const timestamp = Date.now();
      const filePath = `${tenantId}/${timestamp}-${file.name}`;

      const { error: uploadError } = await supabase.storage.from('portfolio-imports').upload(filePath, file);

      if (uploadError) throw uploadError;

      // 2. Call edge function to process
      setStatus('Processing import...');
      const { data: result, error: functionError } = await supabase.functions.invoke('import-portfolio-data', {
        body: {
          file_path: filePath,
          fund_id: data.fund_id,
          tenant_id: tenantId,
          create_submissions: createSubmissions,
        },
      });

      // Handle function errors (400/500 responses)
      if (functionError) {
        // The error response body might be in the context
        const errorResponse = (functionError as any).context;
        if (errorResponse && typeof errorResponse.json === 'function') {
          try {
            // Clone the response before reading to avoid "body already read" errors
            const clonedResponse = errorResponse.clone ? errorResponse.clone() : errorResponse;
            const errorData = await clonedResponse.json();

            if (errorData.error) {
              const errorMessage = errorData.error;
              const suggestions = errorData.suggestions || [];
              const detailedError = suggestions.length > 0 ? `${errorMessage}\n\nSuggestions:\n${suggestions.map((s: string) => `• ${s}`).join('\n')}\n\n${errorData.help || ''}` : errorMessage;
              throw new Error(detailedError);
            }
          } catch (parseError) {
            // If it's our detailed error, re-throw it
            if (parseError instanceof Error && parseError.message.includes('Suggestions:')) {
              throw parseError;
            }
            console.error('Failed to parse error response:', parseError);
          }
        }
        throw functionError;
      }

      // Check if import failed with detailed error
      if (!result.success) {
        const errorMessage = result.error || 'Import failed';
        const suggestions = result.suggestions || [];
        const detailedError = suggestions.length > 0 ? `${errorMessage}\n\nSuggestions:\n${suggestions.map((s: string) => `• ${s}`).join('\n')}\n\n${result.help || ''}` : errorMessage;
        throw new Error(detailedError);
      }

      setStatus('Complete!');

      // Show detailed message if available
      if (result.message) {
        setError(null);
        setStatus(result.message);
        setTimeout(() => {
          onSuccess({ companies: result.companies, contacts: result.contacts });
          onClose();
        }, 3000);
      } else {
        onSuccess({ companies: result.companies, contacts: result.contacts });
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Import Portfolio Companies
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a CSV file with company and/or contact information. Our AI will automatically map the fields.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {error}
        </Alert>
      )}

      {isProcessing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {status}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} id="company-file-upload" disabled={isProcessing} />
        <label htmlFor="company-file-upload">
          <Button variant="outlined" component="span" disabled={isProcessing} fullWidth>
            {file ? file.name : 'Choose File'}
          </Button>
        </label>
      </Box>

      {file && (
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Submission Tracking</FormLabel>
          <RadioGroup value={createSubmissions} onChange={(e) => setCreateSubmissions(e.target.value as 'none' | 'historical')}>
            <FormControlLabel value="none" control={<Radio />} label="Track from today forward only" />
            <FormControlLabel value="historical" control={<Radio />} label={`Create submissions for all periods in ${new Date().getFullYear()}`} />
          </RadioGroup>
        </FormControl>
      )}

      <Box sx={{ display: 'flex', gap: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} fullWidth disabled={isProcessing}>
          Cancel
        </Button>
        <Button onClick={handleImport} variant="contained" fullWidth disabled={!file || isProcessing}>
          {isProcessing ? 'Importing...' : 'Import'}
        </Button>
      </Box>
    </Box>
  );
}
